import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import React from "react";
import { useForm } from "react-hook-form";

import { useToast } from "@/components/ui/use-toast";
import { useI18n } from "@/context";
import { getAllInsuranceCompanies } from "@/pages/insurance-companies/insurance-companies.api";
import { getProducts } from "@/pages/products/products.api";

import { reportsApi } from "./reports.api";
import {
  mapSaleDetailsToSale,
  mapSaleListItemToTSale,
} from "./reports.mappers";
import { TSale, TSaleReportFilters } from "./reports.types";
import { buildSupplierReportFilters } from "./reports-filter.utils";
import { IUseSupplierReports } from "./supplier-reports.hooks.interface";
import {
  supplierReportsFiltersSchema,
  TSupplierReportsFiltersFormData,
} from "./supplier-reports.schema";

export const useSupplierReports = (
  queryEnabled: boolean
): IUseSupplierReports => {
  const { t } = useI18n();
  const { toast } = useToast();

  const [currentPage, setCurrentPage] = React.useState(1);
  const [selectedSale, setSelectedSale] = React.useState<TSale | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = React.useState(false);
  const [filters, setFilters] = React.useState<TSaleReportFilters>({});

  const itemsPerPage = 20;
  const hasAppliedSupplier = Boolean(filters.insurance_company_id);

  const filtersForm = useForm<TSupplierReportsFiltersFormData>({
    resolver: zodResolver(supplierReportsFiltersSchema),
    defaultValues: {
      insurance_company_id: "",
      product_id: "",
      status: "",
      start_date: "",
      end_date: "",
    },
  });

  const {
    data: salesReport,
    isLoading,
    isFetching,
    isError,
    error,
    isSuccess,
  } = useQuery({
    queryKey: ["salesReportSupplier", filters, currentPage],
    queryFn: () =>
      reportsApi.getSalesReport({
        ...filters,
        page: currentPage,
        limit: itemsPerPage,
      }),
    enabled:
      queryEnabled && hasAppliedSupplier && currentPage > 0 && itemsPerPage > 0,
  });

  const getSaleDetailsMutation = useMutation({
    mutationFn: (transactionSearchId: string) =>
      reportsApi.getSaleDetails(transactionSearchId),
    onSuccess: (data) => {
      setSelectedSale(mapSaleDetailsToSale(data));
      setIsDetailsModalOpen(true);
    },
    onError: (err: Error) => {
      toast({
        title: t("generic.error"),
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const { data: insuranceCompanies } = useQuery({
    queryKey: ["insuranceCompanies"],
    queryFn: getAllInsuranceCompanies,
  });

  const { data: productsCatalog } = useQuery({
    queryKey: ["products"],
    queryFn: getProducts,
  });

  const supplierOptions = React.useMemo(() => {
    if (!insuranceCompanies) return [];
    return insuranceCompanies
      .map((ic) => ({
        value: ic.id,
        label: ic.name?.trim() || ic.corporate_name || ic.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
  }, [insuranceCompanies]);

  const watchedSupplierId = filtersForm.watch("insurance_company_id");

  const productOptions = React.useMemo(() => {
    if (!productsCatalog || !watchedSupplierId) return [];
    return productsCatalog
      .filter((p) => p.insurance_company_id === watchedSupplierId && p.name)
      .map((p) => ({
        value: p.id,
        label: p.name as string,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
  }, [productsCatalog, watchedSupplierId]);

  const selectedSupplierLabel = React.useMemo(() => {
    if (!filters.insurance_company_id) return null;
    return (
      supplierOptions.find((o) => o.value === filters.insurance_company_id)
        ?.label ?? null
    );
  }, [filters.insurance_company_id, supplierOptions]);

  const statusOptions = React.useMemo(
    () => [
      { value: "pending", label: t("reports.status.pending") },
      { value: "approved", label: t("reports.status.approved") },
      { value: "failed", label: t("reports.status.failed") },
      { value: "refunded", label: t("reports.status.refunded") },
    ],
    [t]
  );

  const exportReportMutation = useMutation({
    mutationFn: () => {
      const { page: _page, limit: _limit, ...exportFilters } = filters;
      return reportsApi.exportSalesReport(exportFilters);
    },
    onSuccess: (csvText) => {
      const blob = new Blob([csvText], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const slug = selectedSupplierLabel
        ? selectedSupplierLabel.replace(/[^\w-]+/g, "-").slice(0, 40)
        : "seguradora";
      link.download = `relatorio-${slug}-${format(new Date(), "yyyy-MM-dd")}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast({ title: t("reports.export.success") });
    },
    onError: (err: Error) => {
      toast({
        title: t("generic.error"),
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const handleApplyFilters = React.useCallback(
    (data: TSupplierReportsFiltersFormData) => {
      setFilters(buildSupplierReportFilters(data));
      setCurrentPage(1);
    },
    []
  );

  const handleClearFilters = React.useCallback(() => {
    filtersForm.reset({
      insurance_company_id: "",
      product_id: "",
      status: "",
      start_date: "",
      end_date: "",
    });
    setFilters({});
    setCurrentPage(1);
  }, [filtersForm]);

  const paginatedSales = React.useMemo(() => {
    if (!salesReport?.data) return [];
    return salesReport.data.map(mapSaleListItemToTSale);
  }, [salesReport?.data]);

  const handlePageChange = React.useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleOpenDetails = React.useCallback(
    (sale: TSale) => {
      getSaleDetailsMutation.mutate(sale.id);
    },
    [getSaleDetailsMutation]
  );

  const handleCloseDetails = React.useCallback(() => {
    setIsDetailsModalOpen(false);
    setSelectedSale(null);
  }, []);

  const handleExportCsv = React.useCallback(() => {
    if (!hasAppliedSupplier) {
      toast({
        title: t("reports.supplierTab.supplierRequiredTitle"),
        description: t("reports.supplierTab.supplierRequired"),
        variant: "destructive",
      });
      return;
    }
    exportReportMutation.mutate();
  }, [exportReportMutation, hasAppliedSupplier, t, toast]);

  React.useEffect(() => {
    if (error) {
      toast({
        title: t("generic.error"),
        description: error.message,
        variant: "destructive",
      });
    }
  }, [error, toast, t]);

  return {
    paginatedSales,
    currentPage,
    totalPages: salesReport?.metadata?.total_pages || 1,
    totalItems: salesReport?.metadata?.total || 0,
    itemsPerPage,
    filtersForm,
    handleApplyFilters,
    handleClearFilters,
    handlePageChange,
    statusOptions,
    supplierOptions,
    productOptions,
    selectedSupplierLabel,
    hasAppliedSupplier,
    handleExportCsv,
    isExporting: exportReportMutation.isPending,
    selectedSale,
    isDetailsModalOpen,
    handleOpenDetails,
    handleCloseDetails,
    isLoading: hasAppliedSupplier && isLoading,
    isFetching: hasAppliedSupplier && isFetching,
    isError: hasAppliedSupplier && isError,
    error: (error ?? null) as Error | null,
    isSuccess: hasAppliedSupplier && isSuccess,
    showSelectSupplierHint: !hasAppliedSupplier,
  };
};
